// pages/DummyRunner/DummyRunner.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import FeatureRenderer from '../../../Reusables/FeatureRenderer/FeatureRenderer';
import { getDefaultValuesFromFeatures, getFeaturesByProductType } from '../../../../helpers/Features/features';
import { postMethod } from '../../../../utils/api-service/api-service';
import { useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { fetchProductType } from '../../../../utils/api-service/frontend';
import { fetchProductDetails } from '../../../../utils/api-service/get-services';
import { inputPreparers } from '../../../../helpers/DummyRunnerHelper/EngineInputPreprocessor';



// Currency formatter
const formatCurrency = (amount, currency = 'INR') => {
    const formatters = {
        'INR': new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2 }),
        'USD': new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }),
        'GBP': new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }),
        'EUR': new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }),
        'AED': new Intl.NumberFormat('ar-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }),
        'SAR': new Intl.NumberFormat('ar-SA', { style: 'currency', currency: 'SAR', minimumFractionDigits: 2 })
    };

    return formatters[currency]?.format(amount) || `${currency} ${amount.toFixed(2)}`;
};

// Component to display consumption details
const ConsumptionDetails = ({ consumption }) => {
    if (!consumption || Object.keys(consumption).length === 0) return null;

    return (
        <div className="mt-3">
            <h6 className="mb-2">Material Consumption</h6>
            <div className="table-responsive">
                <table className="table table-sm table-bordered">
                    <thead className="table-light">
                        <tr>
                            <th>Material</th>
                            <th>Quantity</th>
                            <th>Unit</th>
                            <th>Calculation</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(consumption.totalConsumption || {}).map(([material, data]) => (
                            <tr key={material}>
                                <td className="text-capitalize">{material.replace(/_/g, ' ')}</td>
                                <td>{data.quantity?.toFixed(2)}</td>
                                <td>{data.unit}</td>
                                <td className="small text-muted">
                                    {consumption.components?.[material]?.formula || '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Component to display component breakdown with consumption
const ComponentBreakdown = ({ components, title, badgeColor = "primary" }) => {
    if (!components || Object.keys(components).length === 0) return null;

    return (
        <div className="mb-4">
            <h6 className="mb-3">{title}</h6>
            {Object.entries(components).map(([component, data]) => {
                if (data.amount === 0) return null;

                return (
                    <div key={component} className="card mb-2">
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                    <strong className="text-capitalize">
                                        {component.replace(/_/g, ' ')}
                                    </strong>
                                    {data.priceType && (
                                        <span className={`badge bg-${badgeColor} ms-2`}>
                                            {data.priceType}
                                        </span>
                                    )}
                                </div>
                                <span className="fw-bold">
                                    {formatCurrency(data.amount_base, data.currency || 'INR')}
                                </span>
                            </div>

                            {data.details?.quantity && (
                                <div className="small text-muted mb-2">
                                    Quantity: {data.details.quantity?.toFixed(2)} {data.details.unit}
                                    {data.details.unitPrice && ` @ ${formatCurrency(data.details.unitPrice, data.currency || 'INR')}/${data.details.unit}`}
                                </div>
                            )}

                            {data.consumption && (
                                <div className="small text-muted">
                                    <i className="bi bi-calculator me-1"></i>
                                    {data.consumption.calculation}
                                    {data.consumption.formula && (
                                        <span className="ms-1">({data.consumption.formula})</span>
                                    )}
                                </div>
                            )}

                            {data.originalBreakdown && Object.keys(data.originalBreakdown).length > 0 && (
                                <div className="mt-2 ps-3 border-start">
                                    {Object.entries(data.originalBreakdown).map(([key, val]) => {
                                        if (typeof val === 'object' && val.amount) {
                                            return (
                                                <div key={key} className="d-flex justify-content-between small">
                                                    <span className="text-capitalize">{key}</span>
                                                    <span>{formatCurrency(val.amount, val.currency || 'INR')}</span>
                                                </div>
                                            );
                                        } else if (typeof val === 'number') {
                                            return (
                                                <div key={key} className="d-flex justify-content-between small">
                                                    <span className="text-capitalize">{key}</span>
                                                    <span>{formatCurrency(val, data.currency || 'INR')}</span>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Component to display measurements
const MeasurementsDisplay = ({ measurements }) => {
    if (!measurements) return null;

    return (
        <div className="mb-4">
            <h6 className="mb-3">Measurements</h6>
            <div className="row g-2">
                {measurements.widthInch && (
                    <div className="col-md-4">
                        <div className="bg-light p-2 rounded text-center">
                            <small className="text-muted d-block">Width</small>
                            <strong>{measurements.widthInch.toFixed(1)}"</strong>
                            <small className="text-muted d-block">({measurements.widthMeter?.toFixed(2)}m)</small>
                        </div>
                    </div>
                )}
                {measurements.heightInch && (
                    <div className="col-md-4">
                        <div className="bg-light p-2 rounded text-center">
                            <small className="text-muted d-block">Height</small>
                            <strong>{measurements.heightInch.toFixed(1)}"</strong>
                            <small className="text-muted d-block">({measurements.heightMeter?.toFixed(2)}m)</small>
                        </div>
                    </div>
                )}
                {measurements.panels && (
                    <div className="col-md-4">
                        <div className="bg-light p-2 rounded text-center">
                            <small className="text-muted d-block">Panels</small>
                            <strong>{measurements.panels}</strong>
                        </div>
                    </div>
                )}
                {measurements.areaSqFt && (
                    <div className="col-md-4">
                        <div className="bg-light p-2 rounded text-center">
                            <small className="text-muted d-block">Area</small>
                            <strong>{measurements.areaSqFt.toFixed(0)} sq ft</strong>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Main DummyRunner Component
const DummyRunner = () => {
    const [parentProductType, setParentProductType] = useState("");
    const [productType, setProductType] = useState("CURTAIN");
    const { id } = useParams();
    const [productId, setProductId] = useState("");
    const [features, setFeatures] = useState([]);
    const [userInputs, setUserInputs] = useState({});
    const [result, setResult] = useState(null);
    const [calculating, setCalculating] = useState(false);
    const [autoCalculate, setAutoCalculate] = useState(true);
    const [showConsumption, setShowConsumption] = useState(false);
    const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(true);
    const [productDetails, setProductDetails] = useState({});

    // Track if initial calculation has been done
    const initialCalcDone = useRef(false);

    const getProductType = async () => {
        try {
            const response = await fetchProductType(id);
            if (response.success) {
                setProductType(response.productType);
                setParentProductType(response?.parentProductType)
                setFeatures(getFeaturesByProductType(response.productType));
            }
        } catch (error) {
            console.log("Error: ", error);
        }
    }

    const getProductDetails = async () => {
        try {
            const response = await fetchProductDetails(id);
            if (response && response.success !== false) {
                setProductDetails(response);
            }
        } catch (error) {
            console.log("Error: ", error);
        }
    }



    useEffect(() => {
        if (id) {
            setProductId(id);
            getProductType();
            getProductDetails();
        }
    }, [id])

    // Set default values from features
    useEffect(() => {
        const defaultValues = getDefaultValuesFromFeatures(features);
        setUserInputs(defaultValues);
    }, [features]);

    // Function to check if a field affects price
    const isPriceAffecting = useCallback((field) => {
        if (field.startsWith('hardware.')) return true;
        if (field.startsWith('location.')) return true;
        const feature = features.find(f => f.slug === field);
        return feature?.affectsPrice === true;
    }, [features]);

    // Function to extract all price-affecting values from userInputs
    const getPriceAffectingValues = useCallback(() => {
        const affecting = {};

        Object.keys(userInputs).forEach(key => {
            if (isPriceAffecting(key)) {
                affecting[key] = userInputs[key];
            }
        });

        if (userInputs.hardware && isPriceAffecting('hardware')) {
            affecting.hardware = userInputs.hardware;
        }

        if (userInputs.location && isPriceAffecting('location')) {
            affecting.location = userInputs.location;
        }

        if (userInputs.dimensions && ['CURTAIN', 'ROMAN_BLIND', 'ROLLER_BLIND', 'WOODEN_BLIND'].includes(productType)) {
            affecting.dimensions = userInputs.dimensions;
        }

        return affecting;
    }, [userInputs, isPriceAffecting, productType]);

    // Debounced calculate function
    const debouncedCalculate = useCallback(
        debounce(async (inputs) => {
            if (!productId) return;

            setCalculating(true);
            try {
                const preparer = inputPreparers[parentProductType];
                // console.log("Inputs: ", inputs);
                const calculatorInput = preparer(inputs);
                // console.log("Calculator Input for", productType, ":", calculatorInput);

                const response = await postMethod(`/products/calculate/${productId}`, {
                    productType: parentProductType,
                    ...calculatorInput
                });

                if (response.success) {
                    setResult(response.result);
                } else {
                    console.error('Calculation failed:', response.message);
                    toast.error(response.message || 'Calculation failed');
                }
            } catch (error) {
                console.error('Error calculating price:', error);
                toast.error('Error calculating price');
            } finally {
                setCalculating(false);
            }
        }, 500),
        [productId, productType]
    );

    // Update the auto-calculation dependencies
    useEffect(() => {
        if (!productId) return;

        const isHardwareComplete = () => {
            if (!userInputs.hardware) return true;
            if (userInputs.hardware.hardwareType === 'NONE') return true;

            const { hardwareType, motorizedType, vendor } = userInputs.hardware;

            if (hardwareType === 'MANUAL') {
                return !!(hardwareType && vendor);
            }

            if (hardwareType === 'MOTORIZED') {
                return !!(hardwareType && motorizedType && vendor);
            }

            return false;
        };

        if (['CURTAIN', 'ROMAN_BLIND', 'ROLLER_BLIND', 'WOODEN_BLIND'].includes(productType)) {
            if (!isHardwareComplete()) return;
        }

        const affectingValues = getPriceAffectingValues();

        if (Object.keys(affectingValues).length === 0) return;

        if (['CURTAIN', 'ROMAN_BLIND', 'ROLLER_BLIND', 'WOODEN_BLIND'].includes(productType)) {
            if (!userInputs.dimensions?.width || !userInputs.dimensions?.height) {
                return;
            }
        }

        if (!initialCalcDone.current) {
            initialCalcDone.current = true;
            setTimeout(() => {
                const preparer = inputPreparers[parentProductType];
                const calculatorInput = preparer(userInputs);
                debouncedCalculate(calculatorInput);
            }, 100);
        } else {
            const preparer = inputPreparers[parentProductType];
            const calculatorInput = preparer(userInputs);
            debouncedCalculate(calculatorInput);
        }

        return () => {
            debouncedCalculate.cancel();
        };
    }, [
        userInputs,
        productId,
        productType,
        debouncedCalculate,
        getPriceAffectingValues
    ]);

    const handleFeatureChange = (slug, value) => {
        console.log(`Slug: ${slug} - Value: `, value);
        setUserInputs(prev => ({
            ...prev,
            [slug]: typeof value === 'boolean' ? Boolean(value) : value
        }));
    };

    // Manual calculate function
    const handleManualCalculate = async () => {
        if (!productId) {
            toast.error('Please enter a product ID');
            return;
        }

        setCalculating(true);
        try {
            const preparer = inputPreparers[parentProductType];
            const calculatorInput = preparer(userInputs);

            const response = await postMethod(`/products/calculate/${productId}`, {
                productType: parentProductType,
                ...calculatorInput
            });

            if (response.success) {
                setResult(response.result);
                toast.success('Price calculated successfully');
            } else {
                toast.error(response.message || 'Calculation failed');
            }
        } catch (error) {
            console.error('Error calculating price:', error);
            toast.error('Error calculating price');
        } finally {
            setCalculating(false);
        }
    };

    // Get display currency from result
    const getDisplayCurrency = () => {
        return result?.displayCurrency || result?.breakdown?.currencyInfo?.targetCurrency || 'INR';
    };

    // Get total amount
    const getTotalAmount = () => {
        return result?.breakdown?.total?.amount || result?.total || 0;
    };

    useEffect(() => {
        console.log("User Inputs: ", userInputs);
    }, [userInputs])

    return (
        <div className="container-fluid py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
            <div className="row">
                {/* Left Column - Input Form */}
                <div className="col-lg-5">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 pt-4 pb-0">
                            <h4 className="mb-0">
                                <i className="bi bi-calculator text-primary me-2"></i>
                                Price Calculator - {productType}
                            </h4>
                        </div>

                        <div className="card-body p-4">
                            {/* Product ID */}
                            <div className="mb-4">
                                {/* <label className="form-label fw-medium">
                                    Product ID <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control bg-light"
                                    disabled={true}
                                    value={productId}
                                    placeholder="Enter product ID"
                                /> */}
                                <div>
                                    Prouduct Name: {productDetails?.name}
                                </div>
                                <div>
                                    Product Code: {productDetails?.code}
                                </div>
                            </div>

                            {/* Auto-calculate toggle */}
                            <div className="mb-3 d-flex align-items-center justify-content-between">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="autoCalculate"
                                        checked={autoCalculate}
                                        onChange={(e) => setAutoCalculate(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="autoCalculate">
                                        Auto-calculate on changes
                                    </label>
                                </div>
                                {calculating && (
                                    <div>
                                        <span className="spinner-border spinner-border-sm text-primary me-1" role="status"></span>
                                        <small className="text-muted">Calculating...</small>
                                    </div>
                                )}
                            </div>

                            <hr className="my-4" />

                            {/* Dynamic Features */}
                            <FeatureRenderer
                                features={features}
                                values={userInputs}
                                onChange={handleFeatureChange}
                                productType={productType}
                                parentProductType={parentProductType}
                            />

                            {/* Calculate Button */}
                            <div className="mt-4">
                                <button
                                    className="btn btn-primary w-100 py-2"
                                    onClick={handleManualCalculate}
                                    disabled={calculating || !productId}
                                >
                                    {calculating ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Calculating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-calculator me-2"></i>
                                            Calculate Price
                                        </>
                                    )}
                                </button>
                            </div>

                            {autoCalculate && (
                                <small className="text-muted d-block mt-2 text-center">
                                    <i className="bi bi-lightning-charge me-1"></i>
                                    Auto-calculating as you change values
                                </small>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column - Results */}
                <div className="col-lg-7">
                    {result ? (
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white border-0 pt-4 pb-0 d-flex justify-content-between align-items-center flex-wrap">
                                <div>
                                    <h4 className="mb-0">
                                        <i className="bi bi-file-text text-success me-2"></i>
                                        Price Calculation Result
                                    </h4>
                                    <small className="text-muted">
                                        Last calculated: {new Date().toLocaleTimeString()}
                                    </small>
                                </div>
                                <div className="mt-2 mt-sm-0">
                                    <span className="badge bg-info me-1">
                                        <i className="bi bi-currency-exchange me-1"></i>
                                        {result.displayCurrency || result.breakdown?.currencyInfo?.targetCurrency}
                                    </span>
                                    <span className="badge bg-secondary">
                                        <i className="bi bi-tag me-1"></i>
                                        {result.priceType || 'selling'} price
                                    </span>
                                </div>
                            </div>

                            <div className="card-body p-4">
                                {/* Total Price */}
                                <div className="text-center mb-4 p-3 bg-success bg-opacity-10 rounded">
                                    <h2 className="display-4 text-success mb-0">
                                        {formatCurrency(getTotalAmount(), getDisplayCurrency())}
                                    </h2>
                                    <p className="text-muted mb-0">Total Price ({getDisplayCurrency()})</p>
                                    {result.breakdown?.currencyInfo?.exchangeRate && (
                                        <small className="text-muted">
                                            Exchange rate: 1 INR = {result.breakdown.currencyInfo.exchangeRate} {getDisplayCurrency()}
                                        </small>
                                    )}
                                </div>

                                {/* Currency Info Banner */}
                                {result.breakdown?.currencyInfo && (
                                    <div className="alert alert-light mb-4 py-2">
                                        <div className="row text-center small">
                                            <div className="col">
                                                <strong>Base Currency</strong><br />
                                                {result.breakdown.currencyInfo.baseCurrency}
                                            </div>
                                            <div className="col">
                                                <strong>Local Currency</strong><br />
                                                {result.breakdown.currencyInfo.localCurrency || '-'}
                                            </div>
                                            <div className="col">
                                                <strong>Display Currency</strong><br />
                                                {result.breakdown.currencyInfo.targetCurrency}
                                            </div>
                                            <div className="col">
                                                <strong>Country</strong><br />
                                                {result.breakdown.currencyInfo.country}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Measurements */}
                                {result.measurements && (
                                    <MeasurementsDisplay measurements={result.measurements} />
                                )}

                                {/* Toggle Buttons */}
                                <div className="btn-group w-100 mb-4">
                                    <button
                                        className={`btn btn-outline-primary ${!showDetailedBreakdown ? 'active' : ''}`}
                                        onClick={() => setShowDetailedBreakdown(false)}
                                    >
                                        <i className="bi bi-pie-chart me-1"></i>
                                        Simple Breakdown
                                    </button>
                                    <button
                                        className={`btn btn-outline-primary ${showDetailedBreakdown ? 'active' : ''}`}
                                        onClick={() => setShowDetailedBreakdown(true)}
                                    >
                                        <i className="bi bi-diagram-3 me-1"></i>
                                        Detailed Breakdown
                                    </button>
                                    <button
                                        className={`btn btn-outline-info ${showConsumption ? 'active' : ''}`}
                                        onClick={() => setShowConsumption(!showConsumption)}
                                    >
                                        <i className="bi bi-box-seam me-1"></i>
                                        {showConsumption ? 'Hide' : 'Show'} Consumption
                                    </button>
                                </div>

                                {/* Simple Breakdown View */}
                                {!showDetailedBreakdown && result.breakdown?.subtotals && (
                                    <div className="mb-4">
                                        <h6 className="mb-3">Price Summary</h6>
                                        <div className="list-group">
                                            {result.breakdown.subtotals.inrTotal && (
                                                <div className="list-group-item d-flex justify-content-between align-items-center">
                                                    <span>
                                                        INR Components (Fabric, Stitching, etc.)
                                                        <small className="text-muted d-block">Converted from INR</small>
                                                    </span>
                                                    <span className="fw-bold">
                                                        {formatCurrency(result.breakdown.subtotals.inrTotal.converted, getDisplayCurrency())}
                                                    </span>
                                                </div>
                                            )}
                                            {result.breakdown.subtotals.localTotal && result.breakdown.subtotals.localTotal.amount > 0 && (
                                                <div className="list-group-item d-flex justify-content-between align-items-center">
                                                    <span>
                                                        Local Components (Hardware, Freight, Installation)
                                                        <small className="text-muted d-block">In {result.breakdown.subtotals.localTotal.currency}</small>
                                                    </span>
                                                    <span className="fw-bold">
                                                        {formatCurrency(result.breakdown.subtotals.localTotal.converted, getDisplayCurrency())}
                                                    </span>
                                                </div>
                                            )}
                                            {result.breakdown.subtotals.overhead && result.breakdown.subtotals.overhead.amount > 0 && (
                                                <div className="list-group-item d-flex justify-content-between align-items-center">
                                                    <span>
                                                        Overhead (5%)
                                                        <small className="text-muted d-block">Manufacturing overhead</small>
                                                    </span>
                                                    <span className="fw-bold">
                                                        {formatCurrency(result.breakdown.subtotals.overhead.converted, getDisplayCurrency())}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="list-group-item d-flex justify-content-between align-items-center bg-light">
                                                <strong>Total</strong>
                                                <strong className="text-success">
                                                    {formatCurrency(getTotalAmount(), getDisplayCurrency())}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Detailed Breakdown View */}
                                {showDetailedBreakdown && result.breakdown?.components && (
                                    <>
                                        <ComponentBreakdown
                                            components={result.breakdown.components.inrComponents}
                                            title="INR Components (Converted to Display Currency)"
                                            badgeColor="primary"
                                        />
                                        <ComponentBreakdown
                                            components={result.breakdown.components.localComponents}
                                            title="Local Currency Components"
                                            badgeColor="warning"
                                        />
                                    </>
                                )}

                                {/* Consumption Details */}
                                {showConsumption && result.consumptionSummary && (
                                    <ConsumptionDetails consumption={result.consumptionSummary} />
                                )}

                                {/* Currency Conversion Log */}
                                {result.currencySummary?.conversions?.length > 0 && (
                                    <div className="mt-4">
                                        <button
                                            className="btn btn-sm btn-link text-muted w-100"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#conversionLog"
                                        >
                                            <i className="bi bi-currency-exchange me-1"></i>
                                            View Currency Conversion Details
                                        </button>
                                        <div className="collapse mt-2" id="conversionLog">
                                            <div className="card card-body bg-light">
                                                <small>
                                                    {result.currencySummary.conversions.map((conv, idx) => (
                                                        <div key={idx} className="mb-1">
                                                            {conv.description}: {conv.originalValue?.toFixed(2)} {conv.from} → {conv.convertedValue?.toFixed(2)} {conv.to}
                                                            <span className="text-muted"> (Rate: {conv.rate})</span>
                                                        </div>
                                                    ))}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="card border-0 shadow-sm bg-light">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-calculator fs-1 text-muted mb-3"></i>
                                <h5 className="text-muted">No calculation yet</h5>
                                <p className="text-muted small">
                                    {autoCalculate
                                        ? "Adjust the inputs above to see the price calculation"
                                        : "Click Calculate Price to see the result"}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DummyRunner;